import React, { useEffect, useRef, useState } from 'react'
import { Group, Rect, Text, Path, Image, Arc } from 'react-konva'
import Konva from 'konva'
import { FlattenedTreeNode, TreeNodeId, NodeContentRenderProps } from '../types'
import {
  DEFAULT_INDENT_WIDTH,
  ICON_SIZE,
  ICON_PADDING,
  ICON_COLOR,
  LABEL_COLOR,
  LABEL_FONT_SIZE,
  LABEL_FONT_FAMILY,
  NODE_HOVER_COLOR,
} from '../constants'
import { Html } from 'react-konva-utils'

const ACTION_BUTTON_AREA_WIDTH = 0 // Width for the actions area on the right

interface TreeNodeProps<T extends Record<string, unknown>> {
  readonly node: FlattenedTreeNode<T>
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly onToggleExpand: (nodeId: TreeNodeId) => void
  readonly renderNodeContent?: (
    props: NodeContentRenderProps<T>,
  ) => React.ReactNode // For framework-agnostic custom content
  readonly onNodeClick?: (
    node: FlattenedTreeNode<T>,
    event: Konva.KonvaEventObject<MouseEvent>,
  ) => void
  readonly onNodeHover: (
    node: FlattenedTreeNode<T> | null,
    event: Konva.KonvaEventObject<MouseEvent> | null,
  ) => void
  readonly isHovered: boolean
  readonly isLoading: boolean
  /** Optional: Callback when the mouse is pressed down on the node, initiating a potential drag. */
  readonly onNodePress?: (
    node: FlattenedTreeNode<T>,
    event: Konva.KonvaEventObject<MouseEvent>,
  ) => void
  readonly icon?: (props: FlattenedTreeNode<T>) => React.ReactNode
  readonly iconSize?: number
  // Add props for drag and drop later
}

const EXPAND_ICON_PATH = 'M 0 0 L 0 8 L 4 4 Z' // Simple triangle for expand
const COLLAPSE_ICON_PATH = 'M 0 0 L 8 0 L 4 4 Z' // Simple triangle for collapse

// Simple Spinner Component
const Spinner: React.FC<{
  x: number
  y: number
  radius: number
  stroke: string
}> = ({ x, y, radius, stroke }) => {
  const arcRef = useRef<Konva.Arc>(null)

  useEffect(() => {
    if (!arcRef.current) return
    const anim = new Konva.Animation((frame) => {
      if (!frame || !arcRef.current?.getStage()) return
      const angleDiff = (frame.timeDiff * 360) / 1000
      arcRef.current.rotation((arcRef.current.rotation() + angleDiff) % 360)
    }, arcRef.current.getLayer() || arcRef.current.getStage())

    anim.start()
    return () => {
      anim.stop()
    }
  }, [])

  return (
    <Arc
      ref={arcRef}
      x={x}
      y={y}
      innerRadius={radius * 0.6}
      outerRadius={radius}
      angle={270}
      fill={stroke}
      rotation={0}
    />
  )
}

/**
 * TreeNode component renders a single node in the Konva-based tree.
 * @template T - The type of custom data associated with the node.
 */
export function TreeNode<T extends Record<string, unknown>>({
  node,
  x,
  y,
  width,
  height,
  onToggleExpand,
  renderNodeContent,
  onNodeClick,
  onNodeHover,
  isHovered,
  isLoading,
  onNodePress,
  icon,
  iconSize,
}: TreeNodeProps<T>): React.ReactElement {
  // Node properties: node.isExpanded, node.isLoading, node.hasAsyncChildren, node.children
  const hasLoadedChildren = node.children && node.children.length > 0
  const canShowExpandCollapseIcon =
    (node.hasAsyncChildren && !hasLoadedChildren && !isLoading) ||
    hasLoadedChildren

  const currentIndent = node.depth * DEFAULT_INDENT_WIDTH

  // --- Define areas ---
  // 1. Expand Area (Left)
  const expandAreaX = currentIndent
  const reservedIconSpace = ICON_SIZE + ICON_PADDING * 2

  // 3. Actions Area (Right)
  const actionsAreaX = width - ACTION_BUTTON_AREA_WIDTH - 15 // 10 = 滚动条宽度 + 5

  // State for loaded image if node.icon.type === 'image'
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null)
  const [imageLoadingError, setImageLoadingError] = useState<boolean>(false)

  useEffect(() => {
    if (
      node.icon &&
      node.icon.type === 'image' &&
      typeof node.icon.content === 'string'
    ) {
      const img = new window.Image()
      img.src = node.icon.content as string
      img.onload = () => {
        setLoadedImage(img)
        setImageLoadingError(false)
      }
      img.onerror = () => {
        console.error(`Failed to load image: ${img.src}`)
        setLoadedImage(null)
        setImageLoadingError(true)
      }
    } else {
      // Reset if icon changes or is not an image type
      setLoadedImage(null)
      setImageLoadingError(false)
    }
  }, [node.icon])

  // --- Calculate positions for custom icon and content area ---
  const afterExpandIconAreaX = expandAreaX + reservedIconSpace
  let contentGroupX = afterExpandIconAreaX // Default X for content if no custom icon
  let customIconKonvaElement: React.ReactNode | null = null

  if (node.icon || icon) {
    const iconWidth = node.icon?.width || (iconSize ?? ICON_SIZE)
    const iconHeight = node.icon?.height || (iconSize ?? ICON_SIZE)
    const customIconDisplayX = afterExpandIconAreaX + ICON_PADDING
    const customIconDisplayY = (height - iconHeight) / 2 // Center the icon vertically within the node height

    if (node.icon) {
      if (node.icon.type === 'image') {
        if (loadedImage && !imageLoadingError) {
          customIconKonvaElement = (
            <Group x={customIconDisplayX} y={customIconDisplayY}>
              <Image
                image={loadedImage}
                width={iconWidth}
                height={iconHeight}
              />
            </Group>
          )
        } else if (imageLoadingError) {
          // Optionally render a placeholder or error indicator for a failed image load
          customIconKonvaElement = (
            <Group x={customIconDisplayX} y={customIconDisplayY}>
              <Rect width={iconWidth} height={iconHeight} fill="#cccccc" />
              <Text
                text="X"
                fill="red"
                fontSize={iconHeight * 0.8}
                width={iconWidth}
                height={iconHeight}
                align="center"
                verticalAlign="middle"
              />
            </Group>
          )
        }
      } else if (node.icon.type === 'html') {
        customIconKonvaElement = (
          // The Html component itself doesn't take x/y directly for Konva positioning.
          // We wrap it in a Konva Group to position it.
          // The content of Html is standard React DOM, so its internal styling will apply.
          <Group x={customIconDisplayX} y={customIconDisplayY}>
            <Html
              divProps={{
                style: {
                  width: `${iconWidth}px`,
                  height: `${iconHeight}px`,
                  display: 'flex', // Helps with centering if content is smaller
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none', // Added to pass events to Konva
                },
              }}
            >
              {node.icon.content}
            </Html>
          </Group>
        )
      } else if (node.icon.type === 'konva') {
        // Assuming node.icon.content is already a Konva-compatible ReactNode
        customIconKonvaElement = (
          <Group x={customIconDisplayX} y={customIconDisplayY}>
            {node.icon.content}
          </Group>
        )
      }
    } else {
      customIconKonvaElement = (
        <Group x={customIconDisplayX} y={customIconDisplayY}>
          <Html
            divProps={{
              style: {
                display: 'flex', // Helps with centering if content is smaller
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none', // Added to pass events to Konva
              },
            }}
          >
            {icon(node)}
          </Html>
        </Group>
      )
    }
    // Update the X offset for the main content to be after the custom icon and its padding
    // Only adjust contentGroupX if an icon is actually going to be rendered
    if (customIconKonvaElement) {
      contentGroupX = customIconDisplayX + iconWidth + ICON_PADDING
    }
  }

  // 2. Content Area (Middle)
  // Content area should start after the reserved space for the icon area, for consistent alignment
  const calculatedContentWidth = Math.max(0, actionsAreaX - contentGroupX)
  const relativeTextY = 0 // Text is vertically aligned within its own height later

  console.log('calculatedContentWidth', calculatedContentWidth)

  const handleExpandClick = (
    event: Konva.KonvaEventObject<MouseEvent>,
  ): void => {
    event.cancelBubble = true
    if (!isLoading) {
      onToggleExpand(node.id)
    }
  }

  const handleNodeClickInternal = (
    event: Konva.KonvaEventObject<MouseEvent>,
  ): void => {
    if (onNodeClick) {
      onNodeClick(node, event)
    }
  }

  const handleMouseEnter = (
    event: Konva.KonvaEventObject<MouseEvent>,
  ): void => {
    onNodeHover(node, event)
    const stage = event.target.getStage()
    if (stage) stage.container().style.cursor = 'pointer'
  }

  const handleMouseLeave = (
    event: Konva.KonvaEventObject<MouseEvent>,
  ): void => {
    onNodeHover(null, event)
    const stage = event.target.getStage()
    if (stage) stage.container().style.cursor = 'default'
  }

  const handleNodeMouseDownInternal = (
    event: Konva.KonvaEventObject<MouseEvent>,
  ): void => {
    // This event will be used to initiate dragging from MagicTree
    if (onNodePress) {
      onNodePress(node, event)
    }
    // We don't call onNodeClick here, as mousedown + mouseup without move = click
    // onNodeClick is handled by the Group's onClick handler.
  }

  // Default content renderer if custom one is not provided
  const defaultNodeContent = (
    <Text
      x={0} // Relative to its group placement
      y={relativeTextY}
      text={node.label}
      fontSize={LABEL_FONT_SIZE}
      fontFamily={LABEL_FONT_FAMILY}
      fill={LABEL_COLOR}
      width={calculatedContentWidth} // Use calculated content area width
      height={height}
      verticalAlign="middle"
    />
  )

  const nodeContentRenderProps: NodeContentRenderProps<T> = {
    node,
    depth: node.depth,
    isExpanded: node.isExpanded,
    hasChildren: hasLoadedChildren || !!node.hasAsyncChildren,
    isHovered,
    isLoading,
    contentX: 0, // X is relative to the content group
    contentY: 0, // Y is relative to the content group
    contentWidth: calculatedContentWidth, // Provide available width to custom renderer
    contentHeight: height, // Provide available height
  }

  return (
    <Group
      x={x}
      y={y}
      onClick={handleNodeClickInternal}
      onTap={handleNodeClickInternal}
      onMouseDown={handleNodeMouseDownInternal}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background for hover effect and click area */}
      <Rect
        width={width}
        height={height}
        fill={isHovered ? NODE_HOVER_COLOR : undefined}
        opacity={isHovered ? 0.5 : 0} // Make hover slightly transparent
      />

      {/* --- 1. Expand/Collapse Area (Clickable Group) --- */}
      {((node.isExpanded && isLoading) ||
        (!isLoading && canShowExpandCollapseIcon)) && (
          <Group
            x={expandAreaX} // Positioned at the start of the indent
            y={0}
            width={reservedIconSpace} // Covers the dedicated space for the expand icon
            height={height}
            onClick={
              !isLoading && canShowExpandCollapseIcon
                ? handleExpandClick
                : undefined
            }
            onTap={
              !isLoading && canShowExpandCollapseIcon
                ? handleExpandClick
                : undefined
            }
            onMouseEnter={(e: Konva.KonvaEventObject<MouseEvent>) => {
              if (!isLoading && canShowExpandCollapseIcon) {
                const stage = e.target.getStage()
                if (stage) stage.container().style.cursor = 'pointer'
              }
            }}
            onMouseLeave={(e: Konva.KonvaEventObject<MouseEvent>) => {
              const stage = e.target.getStage()
              if (stage) stage.container().style.cursor = 'default'
            }}
            listening
          >
            {/* Transparent Rect to ensure a hittable area for the Group */}
            <Rect width={reservedIconSpace} height={height} opacity={0} />

            {/* Optional: Add a visible Rect for debugging the clickable area */}
            {/* <Rect width={reservedIconSpace} height={height} stroke="red" strokeWidth={1} opacity={0.3} /> */}

            {node.isExpanded && isLoading && (
              <Spinner
                x={ICON_PADDING + ICON_SIZE / 2} // Centered within this Group
                y={height / 2} // Centered within this Group
                radius={ICON_SIZE / 2.5}
                stroke={ICON_COLOR}
              />
            )}

            {!isLoading && canShowExpandCollapseIcon && (
              <Path
                x={ICON_PADDING + ICON_SIZE / 2} // Centered within this Group
                y={height / 2} // Centered within this Group
                offsetX={
                  (node.isExpanded && hasLoadedChildren ? 8 * 1.5 : 4 * 1.5) / 2
                }
                offsetY={
                  (node.isExpanded && hasLoadedChildren ? 4 * 1.5 : 8 * 1.5) / 2
                }
                data={
                  node.isExpanded && hasLoadedChildren
                    ? COLLAPSE_ICON_PATH
                    : EXPAND_ICON_PATH
                }
                fill={ICON_COLOR}
                scale={{ x: 1.5, y: 1.5 }}
                listening={false} // The Group handles events, Path is purely visual
              />
            )}
          </Group>
        )}

      {/* --- Render Custom Node Icon --- */}
      {customIconKonvaElement}

      {/* --- 2. Content --- */}
      <Group x={contentGroupX} y={0}>
        {/* Use contentGroupX for positioning */}
        {renderNodeContent ? (
          <Html
            divProps={{
              style: {
                // Apply pointerEvents: 'none' to the div wrapping the custom content
                // This allows mouse events to pass through to the Konva Group underneath.
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: `${calculatedContentWidth}px`, // Ensure it uses the available width
                height: `${height}px`, // Ensure it uses the available height
              },
            }}
          >
            {renderNodeContent(nodeContentRenderProps)}
          </Html>
        ) : (
          defaultNodeContent
        )}
      </Group>

      {/* --- 3. Actions Area (Right) --- */}
      {/* {actionsAreaVisible && (
        <Group x={actionsAreaX} y={(height - ACTION_BUTTON_SIZE) / 2}>
          <Rect
            width={ACTION_BUTTON_SIZE}
            height={ACTION_BUTTON_SIZE}
            fill="lightblue"
            x={0}
            cornerRadius={3}
            onClick={(e) => {
              e.cancelBubble = true
              console.log('Edit action')
            }}
          />
          <Text
            text="E"
            x={ACTION_BUTTON_PADDING}
            y={(ACTION_BUTTON_SIZE - LABEL_FONT_SIZE) / 2}
            fontSize={LABEL_FONT_SIZE}
            fill="black"
            listening={false}
          />
          <Rect
            width={ACTION_BUTTON_SIZE}
            height={ACTION_BUTTON_SIZE}
            fill="lightgreen"
            x={ACTION_BUTTON_SIZE + ACTION_BUTTON_PADDING * 2}
            cornerRadius={3}
            onClick={(e) => {
              e.cancelBubble = true
              console.log('Delete action')
            }}
          />
          <Text
            text="D"
            x={
              ACTION_BUTTON_SIZE +
              ACTION_BUTTON_PADDING * 2 +
              ACTION_BUTTON_PADDING
            }
            y={(ACTION_BUTTON_SIZE - LABEL_FONT_SIZE) / 2}
            fontSize={LABEL_FONT_SIZE}
            fill="black"
            listening={false}
          />
        </Group>
      )} */}
    </Group>
  )
}
